import { yupResolver } from '@hookform/resolvers/yup';
import Add from '@mui/icons-material/Add';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import SearchIcon from '@mui/icons-material/Search';
import { Box, Card, Grid, Stack } from '@mui/material';
import { AxiosResponse } from 'axios';
import { isEmpty, noop } from 'lodash-es';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { FormProvider, useForm } from 'react-hook-form';
import { IoFilterSharp } from 'react-icons/io5';
import {
  getAccessControlSettings,
  getInvitedUsersInWorkspaceAdminForId,
  getUsersByStatusInWorkspace,
  IWorkspace,
  searchInvitesUsingEmail,
  getAssignedUserForClientCount,
  getAssignedUserForClientContactCount,
  getUserTeamsCount,
  fetchWorkspaceRole,
  getUserCountsInWorkspace,
  UserCountsResponseDTO,
  IBusinessUnit,
  getInvitedUsersInWorkspaceList,
  IWorkspaceAdmin,
} from 'wisestep-api';
import {
  WsColors,
  SearchBar,
  WsCard,
  CardVariant,
  EmptyTeamsAndUsersIcon,
  WsListingLoader,
  WsButton,
  WsDrawer,
  WsCheckboxList,
  Label,
  WsTagsList,
  ChipsType,
  WsPagination,
} from 'wisestep-base';
import { object, string } from 'yup';

import { useSKUModalStore } from '../../components/skuModal/SKUModalStore';
import WsCheckBox from '../../components/WsCheckBox/WsCheckBox';
import { useWorkspaceContext } from '../../context';
import { UserAccessAction, useUserAccessAuthz } from '../../hooks/useUserAccessAuthz';
import ErrorLogger from '../../providers/ErrorLogger';
import { success, failed, warning } from '../../utils/NotificationMessages';
import { useNotificationStore } from '../../utils/NotificationStore';
import { handleWorkspaceUsersDownload } from '../dataDownload/helper/PayloadHelper';
import { ICheckBoxListOptions } from '../jobs/assign/components/AssignedRecruiters';
import { StyledFooter } from '../jobs/assign/types';

import InviteUserModal from './components/InviteUserModal';
import InviteUserButton from './components/InviteUsersButton';
import TotalInvitesList from './components/TotalInvitesList';
import UsersBanner from './components/UsersBanner';
import { fetchAllBusinessUnits } from './helper/defaultValues';
import { useTotalInvitesCall } from './helper/hooks';
import { IUserStatistics } from './types/IUserStatistics';

interface IWorkspaceUsersFilters {
  pageSize: number;
  pageNumber: number;
}

const userFilters = {
  pageNumber: 1,
  pageSize: 10,
};

const UsersPage = () => {
  const [invitedUsers, setInvitedUsers] = useState<IWorkspace[]>([]);
  const [query, setQuery] = useState<IWorkspaceUsersFilters>(userFilters);
  const [actionCounts, setActionCounts] = useState<IUserStatistics | null>(null);
  const workspaceContext = useWorkspaceContext();
  const [open, setOpen] = useState<boolean>(false);
  const [openBU, setOpenBU] = useState<boolean>(false);
  const [isLoading, setLoading] = useState<boolean>(false);
  const [roles, setRoles] = useState<string[]>([]);
  const handleClose = () => setOpen(false);
  const setModal = useSKUModalStore((state) => state.setModal);
  const [maxInvites] = useTotalInvitesCall();
  const [pendingUsersLimit, setPendingUsersLimit] = useState<number>(maxInvites);
  const setNotification = useNotificationStore((state) => state.setNotification);
  const [filtersArray, setFiltersArray] = useState<string[]>([]);
  const [search, setSearch] = useState<boolean>(false);
  const [businessUnits, setBusinessUnits] = useState<IBusinessUnit[]>([]);
  const requiresMFA = workspaceContext?.requiresMFA ?? false;
  const [noBUAdded, setNoBUAdded] = useState<boolean>(false);
  const { access: userAccessControl } = useUserAccessAuthz([
    UserAccessAction.INVITE_USERS_ACCESS,
    UserAccessAction.MANAGE_DOWNLOAD_USERS,
  ]);
  const [count, setCount] = useState(0);
  const [totalusercount, setTotalusercount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);

  const inviteAccess = workspaceContext.owns || userAccessControl[0];
  const downloadAccess = workspaceContext.owns || userAccessControl[1];

  const handleOpen = () => {
    if (pendingUsersLimit < 1) setOpen(true);
    else
      setModal({
        object: {
          featureCode: '',
          type: '',
          countType: '',
          consumed: maxInvites,
          balance: pendingUsersLimit,
          actualLimit: maxInvites,
          message: '',
        },
        type: 'count',
      });
  };

  const schema = object().shape({
    invitesSearch: string().email('Not an email'),
  });

  const defaultValues = {
    invitesSearch: '',
    status: [],
    businessUnitName: [],
  };

  const fetchBusinessUnits = async () => {
    const response = await fetchAllBusinessUnits();
    setBusinessUnits(response);
  };

  const handleNoBUAddedChange = (checked: boolean) => {
    setNoBUAdded(checked);
  };

  useEffect(() => {
    fetchBusinessUnits();
  }, []);

  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues: defaultValues,
  });
  const { watch, setValue } = methods;

  const getCountItems = async (meth: () => Promise<AxiosResponse<any, any>>): Promise<any> => {
    let response;
    try {
      response = await meth();
    } catch {
      noop();
    }
    return new Promise((resolve) => {
      resolve(response?.data ?? null);
    });
  };

  const getActions = async () => {
    const result = await Promise.all([
      getCountItems(getUserTeamsCount),
      getCountItems(getAssignedUserForClientCount),
      getCountItems(getAssignedUserForClientContactCount),
    ]);
    return {
      teams: result[0],
      client: result[1],
      contacts: result[2],
    };
  };

  const getInvitedUsers = useCallback(async () => {
    const getActionInners = async () => {
      const result = await Promise.all([
        getCountItems(getUserTeamsCount),
        getCountItems(getAssignedUserForClientCount),
        getCountItems(getAssignedUserForClientContactCount),
      ]);
      return {
        teams: result[0],
        client: result[1],
        contacts: result[2],
      };
    };
    try {
      setLoading(true);
      const response = await getInvitedUsersInWorkspaceAdminForId(
        workspaceContext.id ?? '',
        query.pageNumber,
        query.pageSize
      );
      const workspaceDTO: IWorkspaceAdmin = response.data;
      setTotalusercount(response.data.totalUserCount);
      setInvitedUsers(workspaceDTO.workspaces);
      setCount(workspaceDTO?.totalUserCount ?? 0);
      const actionCount = await getActionInners();
      setActionCounts(actionCount);
      const activeLength = response.data.workspaces.filter(
        (invite) => invite.accepted && !invite.barred
      ).length;
      setPendingUsersLimit(maxInvites - activeLength);
      setLoading(false);
    } catch (e) {
      setLoading(false);
    }
  }, [maxInvites, query, workspaceContext.id]);

  interface IInviteSearchData {
    invitesSearch: string;
  }

  const handleDrawerOpen = () => setOpen(true);
  const handleDrawerOpenBU = () => setOpenBU(true);
  const onClose = () => setOpen(false);
  const onCloseBU = () => setOpenBU(false);

  const handleSearchInvites = async (data: IInviteSearchData) => {
    if (data.invitesSearch !== '') {
      try {
        setLoading(true);
        const response = await searchInvitesUsingEmail(data.invitesSearch);
        setInvitedUsers(response.data.userId != null ? [response.data] : []);
        response.data.userId != null ? setSearch(true) : setSearch(false);
        setTotalusercount(1);
        setCount(1);
        setLoading(false);
        return;
      } catch (e) {
        setLoading(false);
      }
    }
    getInvitedUsers();
  };

  const successHandler = () => getInvitedUsers();
  const [userCounts, setUserCounts] = useState<UserCountsResponseDTO>({
    activated: 0,
    invited: 0,
    deactivated: 0,
  });

  useEffect(() => {
    const getRoles = async () => {
      try {
        setLoading(true);
        const accessData = await getAccessControlSettings(workspaceContext?.id ?? '');
        const data = accessData.data;
        const rolesData: string[] = data.map((obj) => obj.workspaceRolesPK.role);
        const customeRole = await fetchWorkspaceRole();
        const customeRoleName: string[] = customeRole.data.map((obj) => obj.roleName);
        const combinedRoles =
          customeRoleName.length > 0 ? rolesData.concat(customeRoleName) : rolesData;
        setRoles(combinedRoles);
      } catch (e) {
        console.log(e);
      }
    };
    getRoles();
    getUserCounts();
  }, []);

  const getUserCounts = async () => {
    try {
      const res = await getUserCountsInWorkspace();
      const data: UserCountsResponseDTO = res.data;
      setUserCounts(data);
    } catch (e) {
      console.log(e);
    }
  };

  useEffect(() => {
    getInvitedUsers();
  }, [getInvitedUsers]);

  const clearAllFields = () => {
    methods.reset();
    getInvitedUsers();
    setValue('status', []);
    setValue('businessUnitName', []);
    setFiltersArray([]);
  };

  const statusOptions: ICheckBoxListOptions = {};
  statusOptions['all'] = 'All';
  statusOptions['activated'] = 'Activated';
  statusOptions['deactivated'] = 'Deactivated';
  statusOptions['invited'] = 'Invited';

  const selectedStatusIds: string[] = watch('status');
  const selectedBusinessUnitFilter: string[] = watch('businessUnitName');
  const saveStatus = async () => {
    if (selectedStatusIds && selectedStatusIds.length > 0) {
      let updatedFiltersArray: string[] = [];
      if (noBUAdded) {
        updatedFiltersArray.push('No BU');
      }
      updatedFiltersArray = selectedBusinessUnitFilter.concat(updatedFiltersArray);
      setFiltersArray(selectedStatusIds.concat(updatedFiltersArray));
      try {
        const response = await getUsersByStatusInWorkspace(selectedStatusIds);
        const filteredUsers = response.data;
        let filteredResponse: IWorkspace[] = [];
        if (noBUAdded) {
          const objectsWithEmptyBusinessUnit: IWorkspace[] = filteredUsers?.filter((item) => {
            return item.businessUnit.length === 0;
          });
          filteredResponse = objectsWithEmptyBusinessUnit;
        }
        if (selectedBusinessUnitFilter.length > 0) {
          const businessUnitFilteredResponse = response.data.filter((item) => {
            return item.businessUnit.some((unit) => selectedBusinessUnitFilter.includes(unit));
          });
          filteredResponse = filteredResponse.concat(businessUnitFilteredResponse);
        }
        if (selectedBusinessUnitFilter.length === 0 && !noBUAdded) {
          filteredResponse = response.data;
        }

        setInvitedUsers(filteredResponse);
        onClose();
        setNotification(success(`Status added Successfully.`));
      } catch {
        setNotification(failed(`Error in status assign. Please try again.`));
      }
    } else {
      setNotification(warning('Please select minimum one option to filter!'));
    }
  };

  const saveBusinessUnitFilter = async (isNoBUAdded?: boolean) => {
    if (isNoBUAdded || selectedBusinessUnitFilter.length > 0) {
      let updatedFiltersArray: string[] = [];
      if (isNoBUAdded) {
        updatedFiltersArray.push('No BU');
      }
      if (selectedBusinessUnitFilter.length > 0) {
        updatedFiltersArray = updatedFiltersArray.concat(selectedBusinessUnitFilter);
      }
      updatedFiltersArray = selectedStatusIds.concat(updatedFiltersArray);
      setFiltersArray(updatedFiltersArray);

      try {
        const response = await getInvitedUsersInWorkspaceList();
        let filteredResponse: IWorkspace[] = [];
        let filteredUsers: IWorkspace[] = [];
        if (selectedStatusIds.length > 0) {
          const response = await getUsersByStatusInWorkspace(selectedStatusIds);
          filteredResponse = response.data;
        } else {
          filteredResponse = response.data;
        }
        if (isNoBUAdded) {
          const objectsWithEmptyBusinessUnit: IWorkspace[] = filteredResponse?.filter((item) => {
            return item.businessUnit?.length === 0;
          });
          filteredUsers = objectsWithEmptyBusinessUnit;
        }
        if (selectedBusinessUnitFilter.length > 0) {
          const businessUnitFilteredResponse = filteredResponse?.filter((item) => {
            return item.businessUnit?.some((unit) => selectedBusinessUnitFilter.includes(unit));
          });
          filteredUsers = filteredUsers.concat(businessUnitFilteredResponse);
        }
        setInvitedUsers(filteredUsers);
        onCloseBU();
        setNotification(success(`Business Unit Filter added Successfully.`));
      } catch {
        setNotification(failed(`Error in Business Unit Filter assign. Please try again.`));
      }
    } else {
      setNotification(warning('Please select minimum one option to filter!'));
    }
  };

  const handleDownloadAction = async () => {
    const downloadStatus = await handleWorkspaceUsersDownload(selectedStatusIds, requiresMFA);
    setNotification(
      downloadStatus == 'success'
        ? success('Downloaded workspace users')
        : warning('Something went wrong, Please try again.')
    );
  };

  const refreshActionCount = async () => {
    const actionCount = await getActions();
    setActionCounts(actionCount);
  };
  const businessUnitOptions: ICheckBoxListOptions = useMemo(() => {
    const temp = {};
    businessUnits.forEach(
      (businessUnit) =>
        businessUnit.id && businessUnit.name && (temp[businessUnit.id] = businessUnit.name)
    );

    return temp;
  }, [businessUnits]);

  const mapIdsToNames = (selectedIds: string[], options: ICheckBoxListOptions): string[] => {
    return selectedIds.map((id) => options[id] ?? id);
  };
  return (
    <Box>
      <UsersBanner userCounts={userCounts} maxInvites={maxInvites} />
      {!isLoading ? (
        <>
          <FormProvider {...methods}>
            {inviteAccess && (
              <Grid container direction="row" justifyContent="flex-end" my={2}>
                <InviteUserButton
                  onSuccess={() => getInvitedUsers()}
                  roles={roles}
                  pendingUsersLimit={pendingUsersLimit}
                  maxInvites={maxInvites}
                />
              </Grid>
            )}
            <Box bgcolor={WsColors.White} borderRadius={1} px={2} py={0} mb={4}>
              <Stack
                direction={'row'}
                justifyContent={'space-between'}
                spacing={2}
                my={2}
                component="form"
                onSubmit={methods.handleSubmit(handleSearchInvites)}
              >
                <Grid item xs={9.5} pt={2}>
                  <Card sx={{ p: 0.5 }}>
                    <SearchBar placeholder="Search for a user" name="invitesSearch" />
                  </Card>
                </Grid>
                <Grid item xs={3} pt={2}>
                  <Stack direction={'row'} spacing={1} justifyContent="end">
                    <WsButton
                      className="clear-tags"
                      variant="outlined"
                      size="large"
                      type="reset"
                      onClick={clearAllFields}
                    >
                      Clear
                    </WsButton>
                    <WsButton
                      variant="outlined"
                      size="large"
                      type="submit"
                      startIcon={<SearchIcon />}
                    >
                      Search
                    </WsButton>
                  </Stack>
                </Grid>
              </Stack>
              <Grid container item pt={2} pb={2} xs={12}>
                <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
                  {downloadAccess && (
                    <WsButton
                      onClick={handleDownloadAction}
                      startIcon={<FileDownloadOutlinedIcon />}
                      variant="text"
                    >
                      Download as CSV
                    </WsButton>
                  )}

                  <WsButton onClick={handleDrawerOpen} startIcon={<IoFilterSharp />} variant="text">
                    User Status
                  </WsButton>
                  <WsButton
                    onClick={handleDrawerOpenBU}
                    startIcon={<IoFilterSharp />}
                    variant="text"
                  >
                    Business Unit
                  </WsButton>
                </ErrorBoundary>
              </Grid>
            </Box>
          </FormProvider>
          {filtersArray.length > 0 && (
            <Box pb={2}>
              <Label sx={{ fontWeight: 'bold' }}>Results generated for:</Label>
              <Box pb={2}>
                {selectedStatusIds.length > 0 && (
                  <Box pb={1}>
                    <Label>User Status:</Label>
                    <WsTagsList
                      chipArray={selectedStatusIds}
                      count={100}
                      type={ChipsType.PRIMARY}
                      allowDelete={false}
                    />
                  </Box>
                )}
                {(selectedBusinessUnitFilter.length > 0 || noBUAdded) && (
                  <Box pb={1}>
                    <Label>Business Unit:</Label>
                    <WsTagsList
                      chipArray={mapIdsToNames(
                        filtersArray.filter(
                          (item) => selectedBusinessUnitFilter.includes(item) || item === 'No BU'
                        ),
                        businessUnitOptions
                      )}
                      count={100}
                      type={ChipsType.PRIMARY}
                      allowDelete={false}
                    />
                  </Box>
                )}
              </Box>
            </Box>
          )}
          <Box>
            {isEmpty(invitedUsers) && invitedUsers?.length === 0 ? (
              <>
                {!search ? (
                  <WsCard
                    title="No Records found"
                    description="We didn't find any results matching your query. Please change your search and try again."
                    cardType={CardVariant.WITH_OUT_BG}
                    isButton={false}
                  />
                ) : (
                  <>
                    <WsCard
                      title="It's lonely here"
                      description="Add users to your workspace"
                      cardType={CardVariant.WITH_OUT_BG}
                      buttonText="Invite Users"
                      middleComponent={<EmptyTeamsAndUsersIcon />}
                      btnStartIcon={<Add />}
                      isButton={false}
                      onClick={handleOpen}
                    />
                    <InviteUserModal
                      open={open}
                      handleClose={handleClose}
                      onSuccess={successHandler}
                      roles={roles}
                    />
                  </>
                )}
              </>
            ) : (
              <>
                <WsPagination
                  count={Math.ceil(count / query?.pageSize ?? 0)}
                  onChange={(_, page) => {
                    setCurrentPage(page + 1);
                    setQuery({ ...query, pageNumber: page + 1 });
                  }}
                  itemsPerPage={query?.pageSize}
                  pageNumber={currentPage - 1}
                  defaultPage={currentPage}
                  isPageCount={true}
                  type="Users"
                  totalCount={totalusercount}
                />
                <TotalInvitesList
                  invitedUsers={invitedUsers}
                  actionCounts={actionCounts}
                  setInvitedUsers={setInvitedUsers}
                  roles={roles}
                  maxUserLimit={maxInvites}
                  pendingUsersLimit={pendingUsersLimit}
                  setPendingUsersLimit={setPendingUsersLimit}
                  refreshActionCount={refreshActionCount}
                  refreshUsersCount={getUserCounts}
                  businessUnits={businessUnits}
                />
              </>
            )}
          </Box>
          <FormProvider {...methods}>
            <WsDrawer
              open={open}
              onClose={onClose}
              title={'User Status'}
              headerSize="medium"
              size="small"
            >
              <Grid container pl={2}>
                <WsCheckboxList name="status" options={statusOptions} />
              </Grid>
              <StyledFooter>
                <Grid container item justifyContent="end" py={1}>
                  <WsButton variant="contained" size="large" onClick={saveStatus}>
                    Apply
                  </WsButton>
                </Grid>
              </StyledFooter>
            </WsDrawer>
            <WsDrawer
              open={openBU}
              onClose={onCloseBU}
              title={'Business Unit'}
              headerSize="medium"
              size="small"
            >
              <Grid container pl={2}>
                <WsCheckBox
                  name="noBuAdded"
                  label="No BU Added"
                  bold={true}
                  onClick={handleNoBUAddedChange}
                />
                <WsCheckboxList name="businessUnitName" options={businessUnitOptions} />
              </Grid>
              <StyledFooter>
                <Grid container item justifyContent="end" py={1}>
                  <WsButton
                    variant="contained"
                    size="large"
                    onClick={() => saveBusinessUnitFilter(noBUAdded)}
                  >
                    Apply
                  </WsButton>
                </Grid>
              </StyledFooter>
            </WsDrawer>
          </FormProvider>
        </>
      ) : (
        <WsListingLoader />
      )}
    </Box>
  );
};

export default UsersPage;
